import { X } from 'lucide-react';
import { useDAWStore } from '../store/daw-store';

interface SettingsModalProps {
  onClose: () => void;
}

/**
 * Settings Modal Component
 * Requirements: 14.4, 14.5
 * 
 * Allows users to configure:
 * - Auto-save enabled/disabled
 * - Reduce motion preference
 */
export const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const { 
    autoSaveEnabled, 
    setAutoSaveEnabled,
    reduceMotion,
    setReduceMotion
  } = useDAWStore();
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--daw-bg-secondary)] rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--daw-border)]">
          <h2 className="text-lg font-semibold text-[var(--daw-text-primary)]">
            Settings
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--daw-bg-hover)] rounded transition-colors"
          >
            <X className="w-5 h-5 text-[var(--daw-text-secondary)]" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Auto-save Setting */}
          <div>
            <h3 className="text-sm font-medium text-[var(--daw-text-primary)] mb-3">
              Auto-save
            </h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-zinc-900"
              />
              <div>
                <div className="text-sm text-[var(--daw-text-primary)]">
                  Enable auto-save
                </div>
                <div className="text-xs text-[var(--daw-text-secondary)]">
                  Automatically create snapshots every 30 minutes
                </div>
              </div>
            </label>
          </div>
          
          {/* Reduce Motion Setting */}
          <div>
            <h3 className="text-sm font-medium text-[var(--daw-text-primary)] mb-3">
              Accessibility
            </h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={reduceMotion}
                onChange={(e) => setReduceMotion(e.target.checked)}
                className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-zinc-900"
              />
              <div>
                <div className="text-sm text-[var(--daw-text-primary)]">
                  Reduce motion
                </div>
                <div className="text-xs text-[var(--daw-text-secondary)]">
                  Minimize animations and transitions
                </div>
              </div>
            </label>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-[var(--daw-border)]">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[var(--daw-accent-primary)] hover:bg-[var(--daw-accent-hover)] text-white rounded transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
